import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

// NEW: Helper to get the latest completion date between SCAW and NSCAW
function getLatestCompletionDate(threadOrItem) {
  const scawDate = threadOrItem?.scaw_completed_at ? new Date(threadOrItem.scaw_completed_at) : null;
  const nscawDate = threadOrItem?.nscaw_completed_at ? new Date(threadOrItem.nscaw_completed_at) : null;
  if (scawDate && nscawDate) {
    return scawDate > nscawDate ? scawDate : nscawDate;
  } else if (scawDate) {
    return scawDate;
  } else if (nscawDate) {
    return nscawDate;
  }
  return null;
}

export default function UserDetailsPage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Existing user-based stats
  const [annotationCount, setAnnotationCount] = useState(null);
  const [countsByThread, setCountsByThread] = useState([]);
  const [threadList, setThreadList] = useState([]);
  const [activeThreads, setActiveThreads] = useState([]);

  // For exporting
  const [exportFilename, setExportFilename] = useState('');
  const [exportFormat, setExportFormat] = useState('json');

  // NEW: For "annotations between" (total count)
  const [betweenStart, setBetweenStart] = useState('');
  const [betweenEnd, setBetweenEnd] = useState('');
  const [betweenCount, setBetweenCount] = useState(null);

  const [resetClientId, setResetClientId] = useState('');
  const [resetFilename, setResetFilename] = useState('');
  const [resetInfo, setResetInfo] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);

  // ... existing code ...

  // Fetch reset information
  const fetchResetInfo = async () => {
    if (!resetClientId || !resetFilename) {
      setErrorMsg('Please provide both Client ID and Filename');
      return;
    }
    
    try {
      const response = await api.get('/admin/get_reset_info', {
        params: {
          username,
          clientId: resetClientId,
          filename: resetFilename
        }
      });
      setResetInfo(response.data);
      setErrorMsg('');
    } catch (error) {
      console.error(error);
      setErrorMsg(error.response?.data?.detail || 'Error fetching reset information');
      setResetInfo(null);
    }
  };

  // Reset thread for user
  const handleResetThread = async () => {
    if (!resetClientId || !resetFilename || !resetInfo) return;
    
    setIsResetting(true);
    try {
      const response = await api.post('/admin/reset_filename', null, {
        params: {
          username,
          clientId: resetClientId,
          filename: resetFilename
        }
      });
      setResetStatus(response.data);
      setErrorMsg('');
      
      // Refresh data after reset
      fetchAllData();
    } catch (error) {
      console.error(error);
      setErrorMsg(error.response?.data?.detail || 'Error resetting thread');
    } finally {
      setIsResetting(false);
    }
  };

  // On mount, fetch user-specific stats
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      // 1) total user annotation count
      const totalRes = await api.get(`/admin/stats/user-annotation-count/${username}`);
      setAnnotationCount(totalRes.data.annotation_count);

      // 2) annotation count by thread
      const byThreadRes = await api.get(`/admin/stats/user-annotation-count-by-thread/${username}`);
      setCountsByThread(byThreadRes.data.annotation_counts);

      // 3) user’s full thread statuses
      const threadRes = await api.get(`/admin/users/${username}/threads`);
      if (threadRes.data.threads) {
        setThreadList(threadRes.data.threads);
      }

      // 4) active threads
      const activeRes = await api.get(`/admin/users/${username}/active-thread`);
      if (activeRes.data.active_threads) {
        setActiveThreads(activeRes.data.active_threads);
      }
    } catch (error) {
      console.error(error);
      setErrorMsg(error.response?.data?.detail || 'Error fetching user stats.');
    } finally {
      setIsLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Trigger the annotation export (supports both CSV and JSON downloads)
  const handleExport = async () => {
    try {
      let url = `/admin/export/user-annotations?username=${encodeURIComponent(username)}`;
      if (exportFilename.trim()) {
        url += `&filename=${encodeURIComponent(exportFilename.trim())}`;
      }
      url += `&format=${encodeURIComponent(exportFormat)}`;

      const response = await api.get(url, { responseType: 'blob' });
      const fileType = exportFormat === 'csv' ? 'text/csv' : 'application/json';
      const fileExtension = exportFormat === 'csv' ? 'csv' : 'json';
      const blob = new Blob([response.data], { type: fileType });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `annotations_${username}.${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.detail || `Export failed: ${err.message}`);
    }
  };

  // NEW: Fetch count of annotations between two dates
  const [filenameCounts, setFilenameCounts] = useState({}); // NEW

  const fetchBetweenStats = async () => {
    if (!betweenStart || !betweenEnd) {
      setErrorMsg('Please provide both start_date and end_date.');
      return;
    }
    try {
      const url = `/admin/stats/annotations-between?start_date=${betweenStart}&end_date=${betweenEnd}&username=${encodeURIComponent(username)}`;
      const res = await api.get(url);
      setBetweenCount(res.data.total_count || res.data.count || 0);
      setFilenameCounts(res.data.filename_counts || {});
    } catch (error) {
      console.error(error);
      setErrorMsg(error.response?.data?.detail || 'Error fetching annotations between dates.');
    }
  };


  // NEW: Merge `countsByThread` with `threadList` so we can show completion dates
  // and sort by the latest completion date (descending).
  const mergedData = React.useMemo(() => {
    // 1) Merge them - prioritize data from countsByThread (has chat_name, group_tags)
    const merged = countsByThread.map((countItem) => {
      const matchingThread = threadList.find(
        (thr) => thr.filename === countItem.filename
      );
      return {
        filename: countItem.filename,
        count: countItem.count,
        scaw_completed_at: matchingThread?.scaw_completed_at || null,
        nscaw_completed_at: matchingThread?.nscaw_completed_at || null,
        // Prioritize chat_name and group_tags from countItem (from updated API)
        chat_name: countItem.chat_name || matchingThread?.chat_name || null,
        group_tags: countItem.group_tags || matchingThread?.group_tags || [],
        group_tag_other: countItem.group_tag_other || matchingThread?.group_tag_other || null,
        client_id: matchingThread?.client_id || null,
      };
    });

    // 2) Sort descending by latest completion date
    merged.sort((a, b) => {
      const latestA = getLatestCompletionDate(a)?.getTime() || 0;
      const latestB = getLatestCompletionDate(b)?.getTime() || 0;
      return latestB - latestA; // descending
    });

    return merged;
  }, [countsByThread, threadList]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pt: 3,
        pb: 6,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Header with Dark Mode Toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} color="primary">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: mode === 'dark'
                ? 'linear-gradient(45deg, #90caf9 30%, #ce93d8 90%)'
                : 'linear-gradient(45deg, #1976d2 30%, #9c27b0 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {username}
          </Typography>
        </Box>
        <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
          <IconButton onClick={toggleTheme} color="primary" size="large">
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={mode === 'dark' ? 4 : 2}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          background: mode === 'dark'
            ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        }}
      >

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}

        {isLoading ? (
          <Box textAlign="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Annotation Overview */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, #2d2d2d 0%, #1e1e1e 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <CardHeader
                  title="Annotation Overview"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  avatar={
                    <Badge badgeContent={annotationCount ?? 0} color="primary" max={9999}>
                      <CheckCircleIcon color="primary" />
                    </Badge>
                  }
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.08)',
                        border: `1px solid ${mode === 'dark' ? 'rgba(144, 202, 249, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Total Annotations
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {annotationCount ?? 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScheduleIcon fontSize="small" />
                        Active Threads
                      </Typography>
                      {activeThreads.length > 0 ? (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {activeThreads.map((t, idx) => (
                            <Chip
                              key={idx}
                              label={`${t.filename.substring(0, 20)}...`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No active threads
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Annotations by Thread (UPDATED) */}
            <Grid item xs={12}>
              <Card
                sx={{
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, #2d2d2d 0%, #1e1e1e 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <CardHeader
                  title="Annotations by Thread"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  avatar={<TagIcon color="primary" />}
                  subheader={`${mergedData.length} thread(s) with annotations`}
                />
                <CardContent>
                  {mergedData.length === 0 ? (
                    <Typography>No annotated threads found.</Typography>
                  ) : (
                    <List sx={{ width: '100%' }}>
                      {mergedData.map((item, index) => {
                        const latestDate = getLatestCompletionDate(item);
                        const latestDateStr = latestDate
                          ? latestDate.toLocaleString()
                          : 'Not Completed';

                        return (
                          <React.Fragment key={item.filename}>
                            <ListItem
                              alignItems="flex-start"
                              sx={{
                                py: 2.5,
                                px: 2,
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                background: mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.02)'
                                  : 'rgba(0, 0, 0, 0.01)',
                                border: mode === 'dark'
                                  ? '1px solid rgba(255, 255, 255, 0.05)'
                                  : '1px solid rgba(0, 0, 0, 0.05)',
                                mb: 2,
                                '&:hover': {
                                  background: mode === 'dark'
                                    ? 'rgba(144, 202, 249, 0.08)'
                                    : 'rgba(25, 118, 210, 0.04)',
                                  border: mode === 'dark'
                                    ? '1px solid rgba(144, 202, 249, 0.3)'
                                    : '1px solid rgba(25, 118, 210, 0.2)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: mode === 'dark'
                                    ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box>
                                    {item.chat_name && (
                                      <Typography
                                        variant="subtitle1"
                                        component="div"
                                        sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}
                                      >
                                        {item.chat_name}
                                      </Typography>
                                    )}
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 0.5 }}
                                    >
                                      {item.filename}
                                    </Typography>
                                    {item.client_id && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: 'inline-block',
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                          bgcolor: mode === 'dark' ? 'rgba(206, 147, 216, 0.15)' : 'rgba(156, 39, 176, 0.1)',
                                          color: mode === 'dark' ? 'secondary.light' : 'secondary.dark',
                                          fontFamily: 'monospace',
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          mb: 0.5,
                                        }}
                                      >
                                        Client: {item.client_id}
                                      </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                      {item.count} message(s)
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      Last completion: {latestDateStr}
                                    </Typography>
                                    {item.group_tags && item.group_tags.length > 0 && (
                                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                        {item.group_tags.map((tag, idx) => {
                                          const tagColors = [
                                            { name: 'Family', color: 'success' },
                                            { name: 'Friends', color: 'info' },
                                            { name: 'Work/Business', color: 'warning' },
                                            { name: 'Political', color: 'error' },
                                            { name: 'Religious', color: 'secondary' },
                                            { name: 'News', color: 'primary' },
                                          ];
                                          const colorMatch = tagColors.find(tc => tag.includes(tc.name));
                                          const chipColor = colorMatch ? colorMatch.color : 'default';

                                          return (
                                            <Chip
                                              key={idx}
                                              label={tag === 'Other' && item.group_tag_other ? `Other: ${item.group_tag_other}` : tag}
                                              size="small"
                                              color={chipColor}
                                              variant="filled"
                                              sx={{
                                                height: '24px',
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                boxShadow: mode === 'dark'
                                                  ? '0 2px 4px rgba(0, 0, 0, 0.3)'
                                                  : '0 1px 3px rgba(0, 0, 0, 0.12)',
                                              }}
                                            />
                                          );
                                        })}
                                      </Stack>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Thread Statuses */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Thread Statuses" />
                <CardContent>
                  {threadList.length === 0 ? (
                    <Typography>No threads allocated to this user.</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {threadList.map((thr, idx) => (
                        <Grid item xs={12} md={4} key={`${thr.filename}-${idx}`}>
                          <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              <strong>{thr.filename}</strong>
                            </Typography>
                            <Typography variant="body2">
                              <strong>Client:</strong> {thr.client_id}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Status:</strong> {thr.status}
                            </Typography>
                            <Typography variant="body2">
                              <strong>SCAW Completed:</strong>{' '}
                              {thr.scaw_completed
                                ? `Yes (at ${
                                    thr.scaw_completed_at
                                      ? new Date(thr.scaw_completed_at).toLocaleString()
                                      : 'Unknown'
                                  })`
                                : 'No'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>NSCAW Completed:</strong>{' '}
                              {thr.nscaw_completed
                                ? `Yes (at ${
                                    thr.nscaw_completed_at
                                      ? new Date(thr.nscaw_completed_at).toLocaleString()
                                      : 'Unknown'
                                  })`
                                : 'No'}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Annotations Between Dates (FILTER DATE RANGE) */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Annotations Between Dates" />
                <CardContent>
                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={betweenStart}
                      onChange={(e) => setBetweenStart(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={betweenEnd}
                      onChange={(e) => setBetweenEnd(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={fetchBetweenStats}>
                      Fetch
                    </Button>
                  </Box>
                  {betweenCount !== null && (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Total annotations between {betweenStart} and {betweenEnd}: <strong>{betweenCount}</strong>
                    </Typography>

                    {Object.keys(filenameCounts).length > 0 ? (
                      <List dense>
                        {Object.entries(filenameCounts).map(([filename, count]) => (
                          <ListItem key={filename} disablePadding>
                            <ListItemText
                              primary={`${filename}: ${count} annotation${count !== 1 ? 's' : ''}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2">No filename-level breakdown available.</Typography>
                    )}
                  </>
                )}

                </CardContent>
              </Card>
            </Grid>

            {/* Export Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Export Annotations" />
                <CardContent>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="(Optional) Filter by Filename"
                      value={exportFilename}
                      onChange={(e) => setExportFilename(e.target.value)}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel id="export-format-label">Format</InputLabel>
                      <Select
                        labelId="export-format-label"
                        label="Format"
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                      >
                        <MenuItem value="json">JSON</MenuItem>
                        <MenuItem value="csv">CSV</MenuItem>
                      </Select>
                    </FormControl>
                    <Button variant="contained" onClick={handleExport}>
                      Export
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          <Grid item xs={12} sx={{ mt: 4 }}>
          <Card>
            <CardHeader title="Thread Reset Management" />
            <CardContent>
              <Typography variant="body1" gutterBottom>
                Reset a specific thread for this user. This will:
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                <ul>
                  <li>Delete all annotations for this thread</li>
                  <li>Reset completion statuses</li>
                  <li>Set thread status back to "locked"</li>
                </ul>
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Client ID"
                    value={resetClientId}
                    onChange={(e) => setResetClientId(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Filename"
                    value={resetFilename}
                    onChange={(e) => setResetFilename(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button 
                    variant="contained" 
                    onClick={fetchResetInfo}
                    disabled={!resetClientId || !resetFilename}
                  >
                    Fetch Reset Info
                  </Button>
                </Grid>
              </Grid>
              
              {/* Reset Info Display */}
              {resetInfo && (
                <Paper elevation={2} sx={{ p: 2, mt: 3, bgcolor: '#f9f9f9' }}>
                  <Typography variant="h6" gutterBottom>
                    Reset Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography>
                        <strong>Allocated:</strong> {resetInfo.is_allocated ? 'Yes' : 'No'}
                      </Typography>
                      {resetInfo.is_allocated && (
                        <>
                          <Typography>
                            <strong>Current Status:</strong> {resetInfo.current_status}
                          </Typography>
                          <Typography>
                            <strong>Date Range:</strong> {resetInfo.date_range?.start_date || 'N/A'} to {resetInfo.date_range?.end_date || 'N/A'}
                          </Typography>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography>
                        <strong>Annotated Messages:</strong> {resetInfo.annotated_messages}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handleResetThread}
                      disabled={!resetInfo.is_allocated || isResetting}
                      startIcon={isResetting ? <CircularProgress size={20} /> : null}
                    >
                      {isResetting ? 'Resetting...' : 'Reset Thread'}
                    </Button>
                  </Box>
                </Paper>
              )}
              
              {/* Reset Status Display */}
              {resetStatus && (
                <Paper elevation={2} sx={{ p: 2, mt: 3, bgcolor: '#e8f5e9' }}>
                  <Typography variant="h6" gutterBottom>
                    Reset Successful
                  </Typography>
                  <Typography>
                    Deleted {resetStatus.annotations_deleted} annotations and {resetStatus.marks_deleted} marks.
                  </Typography>
                  <Typography>
                    Thread status reset to "locked".
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

          </Grid>
        )}
      </Paper>
    </Box>
  );
}
